import React, { useState } from 'react';
import { Building2, Loader2, Pencil, Trash2, X } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Sheet, SheetContent, SheetTitle } from '../../components/ui/sheet';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '../../components/ui/alert-dialog';
import { toast } from '../../hooks/use-toast';
import { mortgageApi } from '../workspacesApi';
import { errorMessage } from '../errors';
import PhoneInput from '../shared/PhoneInput';
import { phoneDigits, formatPhone } from '../shared/phone';

const KINDS = [
  { value: 'wholesale', label: 'Wholesale' },
  { value: 'correspondent', label: 'Correspondent' },
  { value: 'direct', label: 'Direct' },
  { value: 'private', label: 'Private' },
];
const splitList = (s) => String(s || '').split(',').map(x => x.trim()).filter(Boolean);

const editForm = (l) => ({
  name: l.name || '',
  kind: l.kind || 'wholesale',
  contact_name: l.contact_name || '',
  contact_email: l.contact_email || '',
  contact_phone: phoneDigits(l.contact_phone),
  programs: (l.programs || []).join(', '),
  guideline_notes: l.guideline_notes || '',
});

// Only what differs from the lender as loaded; a blank text field clears it (null / []).
function editDiff(form, l) {
  const was = editForm(l);
  const out = {};
  for (const k of Object.keys(form)) {
    if (form[k] === was[k]) continue;
    if (k === 'programs') out[k] = splitList(form[k]);
    else if (k === 'name' || k === 'kind') out[k] = form[k].trim();
    else out[k] = form[k].trim() === '' ? null : form[k].trim();
  }
  return out;
}

// One lender's inline editor (replaces its row while open).
function LenderEditor({ lender, testIdPrefix, onSaved, onCancel }) {
  const [form, setForm] = useState(() => editForm(lender));
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));
  const id = `${testIdPrefix}-lender-edit`;

  const save = async () => {
    if (!form.name.trim()) { toast({ title: 'Enter a lender name' }); return; }
    const diff = editDiff(form, lender);
    if (Object.keys(diff).length === 0) { onCancel(); return; }
    setSaving(true);
    try {
      const l = await mortgageApi.updateLender(lender.lender_id, diff);
      onSaved(l);
      toast({ title: 'Lender saved', description: l.name });
    } catch (e) {
      toast({ title: 'Save failed', description: errorMessage(e) });
    } finally { setSaving(false); }
  };

  return (
    <div className="py-3 space-y-2 text-sm" data-testid={`${id}-form-${lender.lender_id}`}>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label>Name</Label>
          <Input value={form.name} onChange={(e) => set('name')(e.target.value)} className="h-9 mt-1" data-testid={`${id}-name`} />
        </div>
        <div>
          <Label>Kind</Label>
          <Select value={form.kind} onValueChange={set('kind')}>
            <SelectTrigger className="h-9 mt-1" data-testid={`${id}-kind`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {KINDS.map(k => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Contact name</Label>
          <Input value={form.contact_name} onChange={(e) => set('contact_name')(e.target.value)} className="h-9 mt-1" data-testid={`${id}-contact-name`} />
        </div>
        <div>
          <Label>Contact phone</Label>
          <PhoneInput value={form.contact_phone} onChange={set('contact_phone')} className="h-9 mt-1" data-testid={`${id}-contact-phone`} />
        </div>
        <div className="col-span-2">
          <Label>Contact email</Label>
          <Input value={form.contact_email} onChange={(e) => set('contact_email')(e.target.value)} className="h-9 mt-1" data-testid={`${id}-contact-email`} />
        </div>
        <div className="col-span-2">
          <Label>Programs (comma separated)</Label>
          <Input value={form.programs} onChange={(e) => set('programs')(e.target.value)} className="h-9 mt-1" data-testid={`${id}-programs`} />
        </div>
        <div className="col-span-2">
          <Label>Guideline notes</Label>
          <Textarea value={form.guideline_notes} onChange={(e) => set('guideline_notes')(e.target.value)} rows={3} className="mt-1" data-testid={`${id}-notes`} />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" className="btn-outline" onClick={onCancel} data-testid={`${id}-cancel`}>Cancel</button>
        <button type="button" className="btn-gold" disabled={saving} onClick={save} data-testid={`${id}-save`}>
          {saving && <Loader2 className="animate-spin" size={14} />} Save
        </button>
      </div>
    </div>
  );
}

// Lender directory as a slide-over (from the retired Mortgage Workspace page;
// its window.prompt() add-flow is replaced with an inline name + kind form).
// Ticket 187: each row edits in place and removes behind a confirm. A removed lender leaves the
// directory (the route deactivates it); cards that already use it keep the reference. Editing and
// removing need `pipeline.configure` (`canManage`, the same gate the server applies); adding is open.
export default function LenderPanel({ open, onClose, lenders, canManage = false, onAdded, onUpdated, onDeleted, testIdPrefix = 'pipeline' }) {
  const [name, setName] = useState('');
  const [kind, setKind] = useState('wholesale');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);

  if (!open) return null;

  const add = async () => {
    const n = name.trim();
    if (!n) return;
    setSaving(true);
    try {
      const l = await mortgageApi.createLender({ name: n, kind });
      onAdded?.(l);
      setName('');
      toast({ title: 'Lender added', description: n });
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || String(e) });
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await mortgageApi.deleteLender(removeTarget.lender_id);
      onDeleted?.(removeTarget.lender_id);
      toast({ title: 'Lender removed', description: removeTarget.name });
      setRemoveTarget(null);
    } catch (e) {
      toast({ title: 'Remove failed', description: errorMessage(e) });
    } finally { setRemoving(false); }
  };

  return (
    <>
      <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
        <SheetContent
          showClose={false}
          className="p-0 w-full max-w-md sm:max-w-md border-0 shadow-2xl overflow-y-auto bg-white"
          data-testid={`${testIdPrefix}-lender-panel`}
        >
          <div className="px-6 py-4 border-b border-slate-100 sticky top-0 bg-white z-10 flex items-start justify-between">
            <div className="flex items-center gap-2">
              <Building2 size={16} style={{ color: '#070B5F' }} />
              <SheetTitle className="font-serif text-lg font-bold" style={{ color: '#070B5F' }}>Lender Directory ({lenders.length})</SheetTitle>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-md" data-testid={`${testIdPrefix}-lender-panel-close`}>
              <X size={16} className="text-slate-500" />
            </button>
          </div>

          <div className="px-6 py-4 border-b border-slate-100 space-y-2">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Add lender</div>
            <div className="flex gap-2">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Lender name"
                onKeyDown={(e) => e.key === 'Enter' && add()} className="h-9 flex-1" data-testid={`${testIdPrefix}-lender-name-input`} />
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger className="h-9 w-36" data-testid={`${testIdPrefix}-lender-kind-select`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {KINDS.map(k => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <button disabled={saving || !name.trim()} onClick={add} className="btn-gold h-9 px-3" data-testid={`${testIdPrefix}-lender-add-btn`}>
                {saving ? <Loader2 className="animate-spin" size={14} /> : 'Add'}
              </button>
            </div>
          </div>

          {lenders.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-10 px-6">No lenders yet. Add wholesale/correspondent partners here.</p>
          ) : (
            <ul className="divide-y divide-slate-100 px-6">
              {lenders.map(l => (
                <li key={l.lender_id} data-testid={`${testIdPrefix}-lender-row-${l.lender_id}`}>
                  {canManage && editingId === l.lender_id ? (
                    <LenderEditor lender={l} testIdPrefix={testIdPrefix}
                      onSaved={(saved) => { onUpdated?.(saved); setEditingId(null); }}
                      onCancel={() => setEditingId(null)} />
                  ) : (
                    <div className="py-3 flex items-center justify-between gap-2 text-sm">
                      <div className="min-w-0">
                        <div className="font-semibold" style={{ color: '#070B5F' }}>{l.name}</div>
                        <div className="text-xs text-slate-500">{l.kind} {l.programs?.length ? '· ' + l.programs.join(', ') : ''}</div>
                        {(l.contact_name || l.contact_email || l.contact_phone) && (
                          <div className="text-xs text-slate-500 truncate">
                            {[l.contact_name, l.contact_email, formatPhone(l.contact_phone)].filter(Boolean).join(' · ')}
                          </div>
                        )}
                      </div>
                      {canManage && (
                        <div className="flex items-center gap-1 shrink-0">
                          <button type="button" onClick={() => setEditingId(l.lender_id)} aria-label={`Edit ${l.name}`}
                            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500" data-testid={`${testIdPrefix}-lender-edit-${l.lender_id}`}>
                            <Pencil size={14} />
                          </button>
                          <button type="button" onClick={() => setRemoveTarget(l)} aria-label={`Remove ${l.name}`}
                            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500" data-testid={`${testIdPrefix}-lender-delete-${l.lender_id}`}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!removeTarget} onOpenChange={(o) => { if (!o) setRemoveTarget(null); }}>
        <AlertDialogContent data-testid={`${testIdPrefix}-lender-delete-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removeTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              It leaves the directory and can no longer be picked. Opportunities that already use it keep it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid={`${testIdPrefix}-lender-delete-confirm`} onClick={(e) => { e.preventDefault(); remove(); }} disabled={removing}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
